// TanStack Query hook for dashboard task cards + optimistic mutations.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { decodeClaims } from '@/lib/auth';
import type { BinColor, TaskCardDTO } from '@/types';

export function studentId(): string {
  return decodeClaims()?.sub ?? '';
}

interface UseTaskCardsArgs {
  subjectId?: string;
  bin?: string;
  due?: boolean;
}

function listKey(sid: string, args: UseTaskCardsArgs) {
  return ['task-cards', sid, args.subjectId ?? 'all', args.bin ?? 'all', args.due ? 'due' : 'all'] as const;
}

export function useTaskCards(args: UseTaskCardsArgs = {}) {
  const sid = studentId();
  const queryClient = useQueryClient();
  const key = listKey(sid, args);

  const query = useQuery({
    queryKey: key,
    enabled: Boolean(sid),
    queryFn: () => {
      const qs = new URLSearchParams();
      if (args.subjectId) qs.set('subject', args.subjectId);
      if (args.bin) qs.set('bin', args.bin);
      if (args.due) qs.set('due', 'true');
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return apiGet<TaskCardDTO[]>(`/dashboard/${sid}/task-cards${suffix}`);
    },
  });

  // Optimistically move a card to GREEN; roll back on error.
  const moveToGreen = useMutation({
    mutationFn: (cardId: string) => apiPatch<TaskCardDTO>(`/dashboard/task-cards/${cardId}/move-to-green`),
    onMutate: async (cardId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<TaskCardDTO[]>(key);
      queryClient.setQueryData<TaskCardDTO[]>(key, (old) =>
        (old ?? []).map((c) =>
          c.id === cardId ? { ...c, currentBin: 'GREEN', movedToGreenByStudent: true } : c,
        ),
      );
      return { prev };
    },
    onError: (_err, _cardId, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['task-cards', sid] });
    },
  });

  // SM-2 review: recalled / forgot.
  const review = useMutation({
    mutationFn: (vars: { cardId: string; recalled: boolean }) =>
      apiPost<TaskCardDTO>(`/dashboard/task-cards/${vars.cardId}/review`, { recalled: vars.recalled }),
    onSuccess: (updated) => {
      queryClient.setQueryData<TaskCardDTO[]>(key, (old) =>
        (old ?? []).map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['task-cards', sid] });
    },
  });

  // Generalized self-report move (drag-and-drop to ANY bin column, or an
  // explicit action) — optimistic, same shape as moveToGreen above.
  const moveToBin = useMutation({
    mutationFn: (vars: { cardId: string; targetBin: BinColor }) =>
      apiPatch<TaskCardDTO>(`/dashboard/task-cards/${vars.cardId}/move`, { targetBin: vars.targetBin }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<TaskCardDTO[]>(key);
      queryClient.setQueryData<TaskCardDTO[]>(key, (old) =>
        (old ?? []).map((c) => (c.id === vars.cardId ? { ...c, currentBin: vars.targetBin } : c)),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['task-cards', sid] });
    },
  });

  // One-click MCQ reattempt — grades immediately and reclassifies the bin.
  const reattempt = useMutation({
    mutationFn: (vars: { cardId: string; selectedIdx: number }) =>
      apiPost<{ isCorrect: boolean; card: TaskCardDTO }>(`/dashboard/task-cards/${vars.cardId}/reattempt`, {
        selectedIdx: vars.selectedIdx,
      }),
    onSuccess: ({ card }) => {
      queryClient.setQueryData<TaskCardDTO[]>(key, (old) =>
        (old ?? []).map((c) => (c.id === card.id ? { ...c, ...card } : c)),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['task-cards', sid] });
      // The mastery sheet's Task cards column reads the review queue live.
      void queryClient.invalidateQueries({ queryKey: ['report'] });
    },
  });

  return { ...query, moveToGreen, review, moveToBin, reattempt };
}
